import PropTypes from "prop-types";
import { useMemo, useEffect } from "react";
// material
import { CssBaseline, alpha } from "@mui/material";
import {
  ThemeProvider,
  createTheme,
  StyledEngineProvider,
} from "@mui/material/styles";
//
import { createShape } from "./shape";
import palette from "./palette";
import { createTypography } from "./typography";
import ComponentsOverride from "./overrides";
import { createShadows } from "./shadows";
import { AMIK } from "./amikBrand";
import { useDomainConfig } from "src/hooks/useDomainConfig";

// ----------------------------------------------------------------------

ThemeConfig.propTypes = {
  children: PropTypes.node,
};

// Idempotent: reuse a single <style> node by id instead of appending one on
// every render (which would leak DOM nodes and can hang the tab).
const addCssVariables = (primary, filterColor, isAmik) => {
  const dynamicVariables = `
  :root
  {
    --app-color: ${primary.main};
    --app-filter-color: ${filterColor};
    ${isAmik ? `--amik-gradient: ${AMIK.gradient};` : ""}
    ${isAmik ? `--amik-gradient-soft: ${AMIK.gradientSoft};` : ""}
  }
  `;
  let styleElement = document.getElementById("app-css-variables");
  if (!styleElement) {
    styleElement = document.createElement("style");
    styleElement.id = "app-css-variables";
    document.head.appendChild(styleElement);
  }
  styleElement.textContent = dynamicVariables;
};

const addFaviconIcon = (domainConfig) => {
  const favicon = document.getElementById("favicon");
  if (favicon) favicon.href = domainConfig.faviconUrl;
  document.title = domainConfig.title;
};

// Load Poppins (display) + Inter (body) from Google Fonts, once, only on the
// Amik domain. Inter is the body face; Poppins powers Amik headings/eyebrows.
const addAmikFonts = () => {
  if (document.getElementById("amik-fonts")) return;
  const link = document.createElement("link");
  link.id = "amik-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);
};

export default function ThemeConfig({ children }) {
  const domainConfig = useDomainConfig();
  const isAmik = Boolean(domainConfig.isAmik);

  // Side effects must not run during render — that appended a new DOM node on
  // every render and could exhaust the tab. Run them after commit instead.
  useEffect(() => {
    addFaviconIcon(domainConfig);
    addCssVariables(
      domainConfig.palette.primary,
      domainConfig.filterColor,
      isAmik
    );
    if (isAmik) addAmikFonts();
  }, [domainConfig, isAmik]);

  palette.primary = domainConfig.palette.primary;
  if (domainConfig.palette.secondary) {
    palette.secondary = domainConfig.palette.secondary;
  }
  // Keep brand gradient in sync with the domain primary (was hard-coded to the base blue)
  palette.gradients = {
    ...palette.gradients,
    primary: `linear-gradient(to bottom, ${palette.primary.light}, ${palette.primary.main})`,
  };

  // Brand gradient + ink tokens (Amik only) exposed on the theme palette so
  // components can reference theme.palette.gradients.primary / theme.palette.brand.
  if (isAmik) {
    palette.gradients = { ...palette.gradients, primary: AMIK.gradient };
    palette.brand = { ...AMIK };
  }

  const { shadows, customShadows } = createShadows(isAmik);
  customShadows.primary = `0 8px 16px 0 ${alpha(palette.primary.main, 0.24)}`;

  const themeOptions = useMemo(
    () => ({
      isAmik,
      palette,
      shape: createShape(isAmik),
      typography: createTypography(isAmik),
      shadows,
      customShadows,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAmik]
  );

  const theme = createTheme(themeOptions);
  theme.isAmik = isAmik;
  theme.components = ComponentsOverride(theme);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
